// Data Engineering Course Data
import { FaDatabase } from 'react-icons/fa';
import { FaServer } from 'react-icons/fa';
import { FaCodeBranch } from 'react-icons/fa6';
import { FaCloud } from 'react-icons/fa6';
import { FaSquarePollVertical } from 'react-icons/fa6';
import { FaAward } from 'react-icons/fa6';
import { FaGraduationCap } from 'react-icons/fa6';
import { FaUserTie } from 'react-icons/fa6';
import { FaExchangeAlt } from 'react-icons/fa';
import SQLImg from '../assets/tools/SQL.svg';
import PySparkImg from '../assets/tools/PySpark.svg';
import DatabricksImg from '../assets/tools/Databricks.svg';
import DeltaLakeImg from '../assets/tools/DeltaLake.svg';
import SparkMLImg from '../assets/tools/mllib.webp';
import PythonImg from '../assets/data-science/Python.png';

const dataEngineeringCourse = {
  id: 'data-engineering',
  title: 'Data Engineering Training & Internship Program',
  shortTitle: 'Data Engineering',
  hero: {
    headline: 'Data Engineering Training and Internship - Certificate Program',
    subheadline: 'UK Industry-Microsoft Partnered Program | Scalable Pipelines & Production-Ready Projects | Live Mentorship',
    description: 'Join Int-Elligence Tech Solutions Plc and master the engineering practices employers need: building resilient data pipelines, orchestrating jobs on Databricks, and shipping production-grade transformations with PySpark — all through mentor-led projects designed for the UK market.',
    statistics: [
      { value: '18 Weeks', label: 'structured training and project delivery', ref: 'Program duration' },
      { value: '85%', label: 'of UK employers say data platform skills are a hiring priority', ref: 'Tech Nation 2024' },
      { value: '£40K-£80K', label: 'typical starting salaries for data engineering roles', ref: 'Economic Research Institute' },
      { value: 'Portfolio-first', label: 'Real projects that demonstrate capability to employers', ref: 'Program focus' }
    ]
  },
  importantNotice: {
    title: 'IMPORTANT NOTICE',
    requirements: [
      'Treat this as a career accelerator, not a certificate.',
      'Be prepared for hands-on assignments, code reviews, and production-minded deliverables.',
      'Engage proactively in mentor-led architecture and implementation reviews.'
    ],
    reason: 'Mentors invest 30+ hours per participant; we prioritise candidates committed to building production-minded data engineering skills.'
  },
  programHighlights: {
    duration: '18 Weeks',
    features: [
      {
        icon: <FaDatabase />,
        title: '1. SQL Foundations',
        description: 'Master SQL for modelling, performant queries, and shaping data for downstream pipelines.'
      },
      {
        icon: <FaServer />,
        title: '2. PySpark for Scalable Processing',
        description: 'Author scalable, testable Spark jobs using Python and best practices for distributed workloads.'
      },
      {
        icon: <FaCodeBranch />,
        title: '3. ML with PySpark',
        description: 'Integrate lightweight ML workflows into pipelines and evaluate models at scale using Spark MLlib.'
      },
      {
        icon: <FaCloud />,
        title: '4. Databricks Workflows',
        description: 'Develop collaborative notebooks, schedule jobs, and deploy reliable pipelines on Databricks.'
      },
      {
        icon: <FaSquarePollVertical />,
        title: '5. Real-World Projects',
        description: 'Deliver end-to-end projects that demonstrate your ability to design, build and operate data systems.'
      },
      {
        icon: <FaAward />,
        title: '6. Microsoft-Partnered Certification',
        description: 'Earn a certificate backed by our Microsoft partnership to strengthen your professional profile.'
      }
    ]
  },

  toolsCovered: [
    { name: 'SQL', description: 'Query relational data and prepare datasets for downstream work.', image: SQLImg },
    { name: 'PySpark', description: 'Build distributed processing pipelines for large-scale data.', image: PySparkImg },
    { name: 'Spark MLlib', description: 'Train and deploy ML models within Spark workflows.', image: SparkMLImg },
    { name: 'Databricks', description: 'Develop and orchestrate notebooks, jobs, and collaborative pipelines.', image: DatabricksImg },
    { name: 'Python', description: 'Write robust processing scripts and pipeline logic.', image: PythonImg },
    { name: 'Delta Lake', description: 'Implement reliable storage patterns and ACID-like workflows for analytics.', image: DeltaLakeImg }
  ],
  whyThisProgram: [
    {
      title: 'Engineering-Focused Skills',
      description: 'Learn the tools and patterns used to move, transform and serve data reliably at scale.'
    },
    {
      title: 'Hands-On Pipelines',
      description: 'Hands-on practice across ingestion, transformation, orchestration, and delivery workflows.'
    },
    {
      title: 'Mentor Guidance',
      description: 'Receive architecture and code reviews focused on production-readiness and reliability.'
    },
    {
      title: 'Portfolio Outcomes',
      description: 'Finish with tangible portfolio projects that demonstrate production-minded engineering skill.'
    },
    {
      title: 'Flexible Learning',
      description: 'Follow a structured curriculum that fits around study or work commitments.'
    },
    {
      title: 'UK Industry Alignment',
      description: 'Train with a curriculum aligned to UK employer expectations and hiring standards.'
    }
  ],
  programDetails: {
    overview: {
      title: 'Program Overview and Duration',
      description: 'This 18-week program takes you from core SQL and data modelling to designing, building and operating reliable, scalable data pipelines used by production teams.',
      phases: [
        {
          title: 'Weeks 1-3: SQL',
          description: 'Learn database querying, joins, aggregates, and data shaping for engineering workflows.'
        },
        {
          title: 'Weeks 4-7: PySpark',
          description: 'Use Spark with Python to process large datasets and create scalable transformations.'
        },
        {
          title: 'Weeks 8-11: ML PySpark',
          description: 'Build and evaluate machine learning workflows inside Spark-based environments.'
        },
        {
          title: 'Weeks 12-14: Databricks',
          description: 'Work in Databricks notebooks and jobs to develop collaborative data solutions.'
        },
        {
          title: 'Weeks 15-18: Real-World Projects',
          description: 'Deliver practical data engineering projects that tie together the full stack.'
        }
      ]
    },
    modeOfDelivery: {
      title: 'Mode of Delivery',
      description: 'The program is fully remote and combines self-paced learning, mentor check-ins, and practical assignments. This is a learning-focused, unpaid program built around real-world outcomes and portfolio delivery.'
    },
    commitment: {
      title: 'Commitment and Flexibility',
      description: 'Participants should plan for at least 8–10 hours per week to keep up with lessons, assignments, and project deliverables.'
    },
    curriculum: {
      title: 'Curriculum and Hands-On Experience',
      description: 'You will progress through a practical curriculum focused on pipeline development, observability, and scalable processing.',
      points: [
        'Learn SQL, PySpark, ML PySpark, and Databricks through guided lessons and tasks.',
        'Practice designing repeatable data workflows and transformation logic.',
        'Deliver project work that demonstrates engineering, collaboration, and problem solving.'
      ]
    },
    mentorship: {
      title: 'Mentorship',
      description: 'Weekly 1:1 sessions (30–45 minutes) with UK-based data engineers focusing on architecture advice, code reviews, and career progression.'
    },
    certification: {
      title: 'Certification and Credentials',
      description: 'We are a Microsoft Partner (Partner ID: 6843023). On completion you receive a Microsoft-partnered certificate issued by Int-Elligence Tech Solutions Plc to support UK job applications.'
    },
    careerAdvancement: {
      title: 'Career Advancement',
      description: 'Strong performers may be considered for client work or platform roles. We coach you on technical interviews and how to present engineering trade-offs in interviews.',
      additionalInfo: 'The program emphasises production readiness: code quality, testing, and observability practices employers look for.',
      careerPaths: 'Typical roles include Data Engineer, Analytics Engineer, ETL Developer, Spark Engineer, and Platform Engineer.'
    }
  },
  whoShouldApply: [
    {
      icon: <FaGraduationCap />,
      title: 'Graduates',
      description: 'Build practical engineering skills and a strong project portfolio.'
    },
    {
      icon: <FaUserTie />,
      title: 'Professionals',
      description: 'Upskill into data pipelines, platform work, or engineering roles.'
    },
    {
      icon: <FaExchangeAlt />,
      title: 'Career Switchers',
      description: 'Transition into data engineering with structured, mentor-led learning.'
    }
  ],
  aluminiVideos: [
    {
      id: 1,
      name: 'Yemisi Ohiorenoya',
      videoUrl: 'https://pub-1a559929ef4149c4abff31d55531033a.r2.dev/yemisi%20(2).mp4'
    },
    {
      id: 2,
      name: 'Afifa Anis',
      videoUrl: 'https://pub-1a559929ef4149c4abff31d55531033a.r2.dev/afifa.mp4'
    },
    {
      id: 3,
      name: 'Obanla Oluwaseun',
      videoUrl: 'https://pub-1a559929ef4149c4abff31d55531033a.r2.dev/obanla.mp4'
    }
  ],
  whyAlumniGetHired: [
    {
      icon: '✅',
      title: 'Spark Skills',
      description: 'Graduates can process large datasets and explain pipeline choices clearly.'
    },
    {
      icon: '✅',
      title: 'Databricks Exposure',
      description: 'Hands-on work in Databricks prepares learners for modern data platforms.'
    },
    {
      icon: '✅',
      title: 'Project Evidence',
      description: 'Portfolio work shows practical engineering and delivery capability.'
    }
  ],
  testimonialLink: 'https://share.google/wv45vd8NA35pedVmw',
  contactEmail: 'hr@int-elligence.co.uk',
  footerNote: 'The UK needs more engineers who can build dependable data platforms.'
};

export default dataEngineeringCourse;